import { Dices } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { ArtworkThumb } from "../cards/ArtworkThumb"
import { CostBadge } from "../cards/CostBadge"
import {
  CARD_LIBRARY_STORAGE_KEY,
  CARD_LIBRARY_STORAGE_VERSION,
  CardLibraryStateSchema,
  EMPTY_CARD_LIBRARY,
} from "../cards/storage"
import { useUserIdbState } from "../hooks/useIdbState"
import { getFactionName } from "../i18n/gameContent"
import { useI18n } from "../i18n/useI18n"
import { validateDeck } from "../match/engine/validator/decks"
import type {
  CardColor,
  CardLibraryStore,
  DeckEntry,
  HeroLibraryStore,
  ManagedDeckRecord,
} from "../schemas"
import { buildRandomDeck } from "./randomDeck"
import { createDeckId } from "./storage"
import { getDeckValidationIssueLabel } from "./validationMessages"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Props = {
  open: boolean
  onClose: () => void
  editingRecord: ManagedDeckRecord | null
  heroes: HeroLibraryStore
  onCreateHero: () => void
  onSave: (record: ManagedDeckRecord) => void
}

type EntryMap = Map<string, number> // cardId -> count

function entriesToMap(entries: DeckEntry[]): EntryMap {
  return new Map(entries.map((e) => [e.cardId, e.count]))
}

function mapToEntries(map: EntryMap): DeckEntry[] {
  return [...map.entries()]
    .filter(([, count]) => count > 0)
    .map(([cardId, count]) => ({ cardId, count }))
}

function formatFactionPair(
  t: ReturnType<typeof useI18n>["t"],
  primaryColor: CardColor,
  secondaryColor?: CardColor,
): string {
  const primaryLabel = getFactionName(t, primaryColor)
  if (!secondaryColor) return primaryLabel
  return `${primaryLabel} / ${getFactionName(t, secondaryColor)}`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DeckEditorModal({
  open,
  onClose,
  editingRecord,
  heroes,
  onCreateHero,
  onSave,
}: Props) {
  const { t } = useI18n()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState(editingRecord?.deck.name ?? "")
  const [heroId, setHeroId] = useState(editingRecord?.deck.heroId ?? "")
  const [entryMap, setEntryMap] = useState<EntryMap>(() =>
    entriesToMap(editingRecord?.deck.mercEntries ?? []),
  )
  const [errors, setErrors] = useState<string[]>([])
  const [cardFilter, setCardFilter] = useState("")
  const [colorPreference, setColorPreference] = useState<CardColor | "">("")

  const [cards, cardsLoading] = useUserIdbState(
    CARD_LIBRARY_STORAGE_KEY,
    CardLibraryStateSchema,
    { defaultValue: EMPTY_CARD_LIBRARY, version: CARD_LIBRARY_STORAGE_VERSION },
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: open is a prop — reset on reopen
  useEffect(() => {
    setName(editingRecord?.deck.name ?? "")
    setHeroId(editingRecord?.deck.heroId ?? "")
    setEntryMap(entriesToMap(editingRecord?.deck.mercEntries ?? []))
    setErrors([])
    setCardFilter("")
    setColorPreference("")
  }, [editingRecord, open])

  const handleRandomDeck = () => {
    const allAuthored = (cards as CardLibraryStore)
      .filter((r) => r.card.kind === "authored_unit")
      .map((r) => r.card)
    const result = buildRandomDeck(allAuthored, colorPreference || undefined)
    setEntryMap(result)
    setErrors([])
  }

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  }, [open])

  const handleDialogClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose()
  }

  const totalCards = [...entryMap.values()].reduce((s, n) => s + n, 0)
  const uniqueEntries = [...entryMap.entries()].filter(([, n]) => n > 0).length

  const setCount = (cardId: string, delta: number) => {
    setEntryMap((prev) => {
      const next = new Map(prev)
      const current = next.get(cardId) ?? 0
      const newCount = Math.min(3, Math.max(0, current + delta))
      if (newCount === 0) next.delete(cardId)
      else next.set(cardId, newCount)
      return next
    })
  }

  // Only authored units can be in a deck (not starters, bots)
  const authoredCards = (cards as CardLibraryStore).filter(
    (r) => r.card.kind === "authored_unit",
  )
  const authoredCardMap = new Map(
    authoredCards.map((record) => [record.card.id, record.card]),
  )

  const filteredCards = cardFilter
    ? authoredCards.filter((r) =>
        r.card.name.toLowerCase().includes(cardFilter.toLowerCase()),
      )
    : authoredCards
  const selectedHero = heroes.find((record) => record.hero.heroId === heroId)

  const handleSave = () => {
    const errs: string[] = []
    if (!name.trim()) errs.push(t("deckEditor.error.deckNameRequired"))

    const deck = {
      deckId: editingRecord?.deck.deckId ?? createDeckId(name.trim()),
      name: name.trim(),
      heroId,
      mercEntries: mapToEntries(entryMap),
    }
    const validationErrors = validateDeck(deck, authoredCardMap)
      .issues.filter((issue) => issue.severity === "error")
      .map((issue) => getDeckValidationIssueLabel(t, issue))
    errs.push(...validationErrors)

    if (errs.length > 0) {
      setErrors(errs)
      return
    }

    const record: ManagedDeckRecord = {
      deck,
      remoteSyncFailedAt: undefined,
      remoteSyncedAt: undefined,
      updatedAt: new Date().toISOString(),
    }
    setErrors([])
    onSave(record)
  }

  const cardCountColor =
    totalCards === 24
      ? "count-ok"
      : totalCards > 24
        ? "count-over"
        : "count-under"

  return (
    <dialog
      className="card-editor-dialog deck-editor-dialog"
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      onMouseDown={handleDialogClick}
      ref={dialogRef}
    >
      <div className="card-editor-inner">
        <div className="card-editor-header">
          <h2>
            {editingRecord
              ? t("deckEditor.editTitle")
              : t("deckEditor.createTitle")}
          </h2>
          <button
            aria-label={t("deckEditor.closeAria")}
            className="ghost-icon-button"
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>

        <form
          className="card-editor-form"
          onSubmit={(e) => {
            e.preventDefault()
            handleSave()
          }}
        >
          {/* Deck name */}
          <div className="field">
            <label htmlFor="deck-name">
              <span>{t("deckEditor.deckNameLabel")}</span>
            </label>
            <input
              autoFocus
              className="text-input"
              id="deck-name"
              onChange={(e) => setName(e.target.value)}
              placeholder={t("deckEditor.deckNamePlaceholder")}
              type="text"
              value={name}
            />
          </div>

          {/* Hero */}
          <div className="field">
            <label htmlFor="deck-hero">
              <span>{t("common.hero")}</span>
            </label>
            <div className="hero-select-row">
              <select
                className="text-input"
                id="deck-hero"
                onChange={(e) => setHeroId(e.target.value)}
                value={heroId}
              >
                <option value="">{t("deckEditor.heroPlaceholder")}</option>
                {heroes.map((r) => (
                  <option key={r.hero.heroId} value={r.hero.heroId}>
                    {r.hero.name} (
                    {formatFactionPair(
                      t,
                      r.hero.primaryColor,
                      r.hero.secondaryColor,
                    )}
                    )
                  </option>
                ))}
              </select>
              <button
                className="secondary-button"
                onClick={onCreateHero}
                type="button"
              >
                {t("deckEditor.addHero")}
              </button>
            </div>
            {selectedHero && (
              <div className="deck-hero-preview">
                <ArtworkThumb
                  artwork={selectedHero.hero.progression[0].card.artwork}
                  name={selectedHero.hero.name}
                  primaryColor={selectedHero.hero.primaryColor}
                  size="m"
                />
                <div className="identity-cell">
                  <strong>{selectedHero.hero.name}</strong>
                  <small>
                    {formatFactionPair(
                      t,
                      selectedHero.hero.primaryColor,
                      selectedHero.hero.secondaryColor,
                    )}
                  </small>
                </div>
              </div>
            )}
          </div>

          {/* Card picker */}
          <fieldset className="skills-fieldset card-picker-fieldset">
            <legend>
              {t("common.cards")} —{" "}
              <span className={cardCountColor}>
                {t("deckEditor.cardCountSummary", {
                  total: totalCards,
                  unique: uniqueEntries,
                })}
              </span>
            </legend>

            <div className="deck-random-row">
              <select
                className="text-input deck-random-color-select"
                onChange={(e) =>
                  setColorPreference(e.target.value as CardColor | "")
                }
                value={colorPreference}
              >
                <option value="">{t("deckEditor.colorPreferenceAny")}</option>
                {(
                  ["red", "yellow", "green", "blue", "purple"] as CardColor[]
                ).map((color) => (
                  <option key={color} value={color}>
                    {getFactionName(t, color)}
                  </option>
                ))}
              </select>
              <button
                className="secondary-button"
                disabled={cardsLoading}
                onClick={handleRandomDeck}
                title={t("deckEditor.randomDeck")}
                type="button"
              >
                <Dices size={14} strokeWidth={2} />
                {t("deckEditor.randomDeck")}
              </button>
            </div>

            <div className="field">
              <input
                className="text-input"
                onChange={(e) => setCardFilter(e.target.value)}
                placeholder={t("deckEditor.filterPlaceholder")}
                type="search"
                value={cardFilter}
              />
            </div>

            <div className="card-picker-list">
              {filteredCards.map((r) => {
                const count = entryMap.get(r.card.id) ?? 0
                return (
                  <div className="card-picker-row" key={r.card.id}>
                    <div className="card-picker-info">
                      <ArtworkThumb
                        artwork={r.card.artwork}
                        className="card-picker-artwork"
                        name={r.card.name}
                        primaryColor={r.card.primaryColor}
                        size="s"
                      />
                      <span className="card-picker-name">{r.card.name}</span>
                      {r.card.derivedCost != null ? (
                        <CostBadge
                          className="card-picker-cost"
                          cost={r.card.derivedCost}
                          size="s"
                          variant="subtle"
                        />
                      ) : (
                        <span className="card-picker-cost">?</span>
                      )}
                    </div>
                    <div className="card-picker-controls">
                      <button
                        aria-label={t("deckEditor.removeOneAria", {
                          name: r.card.name,
                        })}
                        className="count-button"
                        disabled={count === 0}
                        onClick={() => setCount(r.card.id, -1)}
                        type="button"
                      >
                        −
                      </button>
                      <span className="count-display">{count}</span>
                      <button
                        aria-label={t("deckEditor.addOneAria", {
                          name: r.card.name,
                        })}
                        className="count-button"
                        disabled={count >= 3}
                        onClick={() => setCount(r.card.id, 1)}
                        type="button"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )
              })}
              {filteredCards.length === 0 && (
                <p className="muted-cell picker-empty">
                  {t("deckEditor.emptyFilter")}
                </p>
              )}
            </div>
          </fieldset>

          {errors.length > 0 && (
            <ul className="form-errors">
              {errors.map((e) => (
                <li className="field-error" key={e}>
                  {e}
                </li>
              ))}
            </ul>
          )}

          <div className="card-editor-actions">
            <button
              className="secondary-button"
              onClick={onClose}
              type="button"
            >
              {t("common.cancel")}
            </button>
            <button className="primary-button" type="submit">
              {editingRecord
                ? t("common.saveChanges")
                : t("table.decks.create")}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  )
}
